/**
 * BprGraphPriorityView - the D1 inverted triangle: findings bucketed into the
 * deterministic Critical / High / Medium / Low tiers (apex first), each card
 * carrying its hard-override badge, score, root-cause data nodes ("root cause
 * rises"), and the within-tier effort sequencing hint.
 */

import type {
  BprGraphDiagram,
  BprGraphPriorityViewItem,
  BprGraphScoreComponent,
} from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import { Pill } from '@protoss/ui';
import * as React from 'react';

const TIER_MODIFIER: Record<string, string> = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

function tierVariant(tier: BprGraphPriorityViewItem['tier']): PillVariant {
  switch (tier) {
    case 'Critical':
      return 'danger';
    case 'High':
      return 'warning';
    case 'Medium':
      return 'accent';
    default:
      return 'muted';
  }
}

function ScoreRow({ component }: { component: BprGraphScoreComponent }) {
  const [showDependents, setShowDependents] = React.useState(false);
  const dependents = component.dependents ?? [];
  const canExpand = component.active && dependents.length > 0;

  const label = (
    <span className="bpr-graph-priority__score-row-label">
      {component.label}
      {component.detail ? (
        <span className="bpr-graph-priority__score-row-detail">{component.detail}</span>
      ) : null}
    </span>
  );

  return (
    <li
      className={`bpr-graph-priority__score-row${
        component.active ? '' : ' bpr-graph-priority__score-row--inactive'
      }`}
    >
      <div className="bpr-graph-priority__score-row-main">
        {canExpand ? (
          <button
            type="button"
            className="bpr-graph-priority__score-row-toggle"
            aria-expanded={showDependents}
            onClick={() => setShowDependents((v) => !v)}
          >
            <span className="bpr-graph-priority__score-row-caret" aria-hidden="true">
              {showDependents ? '▾' : '▸'}
            </span>
            {label}
          </button>
        ) : (
          label
        )}
        <span className="bpr-graph-priority__score-row-points">
          {component.active ? `+${component.points}` : '0'}
        </span>
      </div>
      {canExpand && showDependents ? (
        <ul className="bpr-graph-priority__dependents">
          {dependents.map((dependent) => (
            <li key={dependent.id} className="bpr-graph-priority__dependent">
              {dependent.label}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function ScoreBreakdown({
  breakdown,
}: {
  breakdown: NonNullable<BprGraphPriorityViewItem['score_breakdown']>;
}) {
  return (
    <div className="bpr-graph-priority__score-detail" role="region" aria-label="Score breakdown">
      <p className="bpr-graph-priority__score-intro">
        Deterministic weighted score - each signal below adds points; the total sets the tier.
      </p>
      <ul className="bpr-graph-priority__score-rows">
        {breakdown.components.map((component) => (
          <ScoreRow key={component.key} component={component} />
        ))}
        <li className="bpr-graph-priority__score-row bpr-graph-priority__score-row--total">
          <div className="bpr-graph-priority__score-row-main">
            <span className="bpr-graph-priority__score-row-label">
              Total (tier threshold {breakdown.tier_min})
            </span>
            <span className="bpr-graph-priority__score-row-points">{breakdown.total}</span>
          </div>
        </li>
      </ul>
    </div>
  );
}

function ItemCard({ item }: { item: BprGraphPriorityViewItem }) {
  const [showScore, setShowScore] = React.useState(false);
  const canExplain = !item.hard_override && item.score_breakdown !== null;
  return (
    <li className="bpr-graph-priority__item">
      <div className="bpr-graph-priority__item-head">
        <span className="bpr-graph-priority__item-label">{item.label}</span>
        {item.hard_override ? (
          <Pill variant={item.hard_override === 'escalated' ? 'danger' : 'muted'}>
            {item.hard_override === 'escalated' ? 'Client escalated' : 'Client deprioritized'}
          </Pill>
        ) : canExplain ? (
          <button
            type="button"
            className="bpr-graph-priority__score-btn"
            aria-expanded={showScore}
            onClick={() => setShowScore((v) => !v)}
            title="Show how this score is calculated"
          >
            <Pill variant="neutral">score {item.score}</Pill>
          </button>
        ) : (
          <Pill variant="neutral">score {item.score}</Pill>
        )}
        {item.effort ? <Pill variant="ghost">effort {item.effort}</Pill> : null}
      </div>
      {showScore && item.score_breakdown ? (
        <ScoreBreakdown breakdown={item.score_breakdown} />
      ) : null}
      {item.root_causes.length > 0 ? (
        <div className="bpr-graph-priority__causes">
          <span className="bpr-graph-priority__causes-label">Root cause:</span>
          {item.root_causes.map((cause) => (
            <span key={cause.id} className="bpr-graph-priority__cause">
              {cause.label}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

export interface BprGraphPriorityViewProps {
  priorityView: BprGraphDiagram['priority_view'];
}

export function BprGraphPriorityView({ priorityView }: BprGraphPriorityViewProps) {
  const itemsByTier = React.useMemo(() => {
    const map = new Map<string, BprGraphPriorityViewItem[]>();
    for (const item of priorityView.items) {
      const list = map.get(item.tier) ?? [];
      list.push(item);
      map.set(item.tier, list);
    }
    return map;
  }, [priorityView.items]);

  if (priorityView.items.length === 0) {
    return <p className="bpr-graph__empty">The graph has no findings to prioritize yet.</p>;
  }

  return (
    <div className="bpr-graph-priority">
      {priorityView.tiers.map((tier) => {
        const items = itemsByTier.get(tier) ?? [];
        return (
          <section
            key={tier}
            className={`bpr-graph-priority__tier bpr-graph-priority__tier--${TIER_MODIFIER[tier] ?? 'low'}`}
          >
            <header className="bpr-graph-priority__tier-head">
              <Pill variant={tierVariant(tier)}>{tier}</Pill>
              <span className="bpr-graph-priority__tier-count">
                {items.length} finding{items.length === 1 ? '' : 's'}
              </span>
            </header>
            {items.length === 0 ? (
              <p className="bpr-graph-priority__tier-empty">None</p>
            ) : (
              <ul className="bpr-graph-priority__items">
                {items.map((item) => (
                  <ItemCard key={item.finding_id} item={item} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
